'use client'

import Link from 'next/link'
import { ArrowRight, Zap, Clock, TrendingUp } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { analytics } from '@/lib/analytics'

export default function BusinessOwnersPage() {
  return (
    <div className="bg-background text-charcoal">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-background py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto animate-fade-in">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              AI that actually helps
              <br />
              <span className="text-primary">your business run.</span>
            </h1>
            <p className="text-lg sm:text-xl text-warm-gray mb-8 leading-relaxed">
              Stop wasting hours on email templates, customer replies, and repetitive tasks. Let AI handle the busy work—without needing a tech degree.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link href="/signup" onClick={() => analytics.ctaClicked({ location: 'hero', cta: 'signup', persona: 'business_owner' })}>
                <Button className="bg-primary hover:bg-primary-700 text-white px-8 py-6 text-lg">
                  Get Started Free
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Button variant="outline" className="border-warm-gray text-charcoal hover:bg-primary/5 px-8 py-6 text-lg" asChild>
                <a href="#for-you" onClick={() => analytics.ctaClicked({ location: 'hero', cta: 'learn_more', persona: 'business_owner' })}>See what you can do</a>
              </Button>
            </div>
            <p className="text-sm text-warm-gray mt-4">No credit card required &middot; 5 free sessions included</p>
          </div>
        </div>
      </section>

      {/* For You Section */}
      <section id="for-you" className="py-20 sm:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4">Built for people like you</h2>
            <p className="text-warm-gray text-lg">Real business problems. Real AI solutions.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Efficiency */}
            <div className="p-8 bg-background rounded-lg border border-primary/10">
              <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-primary/10 text-primary mb-4">
                <Clock className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Save 5+ hours a week</h3>
              <p className="text-warm-gray leading-relaxed">
                Cut through email clutter, auto-generate responses, and let AI draft common replies. Spend your time on what actually grows your business.
              </p>
            </div>

            {/* No Tech Required */}
            <div className="p-8 bg-background rounded-lg border border-primary/10">
              <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-primary/10 text-primary mb-4">
                <Zap className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-semibold mb-3">No tech skills required</h3>
              <p className="text-warm-gray leading-relaxed">
                You don't need to know ChatGPT. We explain everything in plain English, one step at a time. If we say it's possible, you can do it.
              </p>
            </div>

            {/* Scale Smart */}
            <div className="p-8 bg-background rounded-lg border border-primary/10">
              <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-primary/10 text-primary mb-4">
                <TrendingUp className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Compete with bigger teams</h3>
              <p className="text-warm-gray leading-relaxed">
                Automate tasks that usually take a full-time hire. Get the leverage of a bigger operation without the payroll.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-20 sm:py-28 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">What you can actually do</h2>
            <p className="text-warm-gray text-lg">Real examples from people who run real businesses</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                title: 'Draft customer responses in seconds',
                description: 'Paste an email. Get a professional reply in 10 seconds. Tweak it or send it—up to you. No more staring at a blank screen.',
                example: 'Customer: "I have a complaint about my order"\nYou: Get 3 response options that sound like you, not a robot.'
              },
              {
                title: 'Create email templates that fit your brand',
                description: 'Build templated responses for common questions (refunds, delivery, FAQs). Customize once, use forever.',
                example: `AI drafts: "Thanks for your order! Here's what to expect next..."\nYou save it. Use it 100 times this month.`
              },
              {
                title: 'Write job postings that actually attract people',
                description: 'Need to hire? AI writes a posting that describes your culture, not just duties. Attracts better candidates.',
                example: 'Old way: 30 mins of painful writing\nAI way: 2 minutes, and it sounds like you.'
              },
              {
                title: 'Turn messy notes into professional memos',
                description: 'Have voice notes or rough thoughts? AI turns them into client emails, internal docs, or social posts.',
                example: 'Your voice: "Uh... orders are up, shipping is slow, team is tired"\nAI memo: Professional update for your team.'
              }
            ].map((useCase, i) => (
              <div key={i} className="p-8 bg-white rounded-lg border border-gray-200">
                <h3 className="text-xl font-semibold mb-3 text-charcoal">{useCase.title}</h3>
                <p className="text-warm-gray mb-6 leading-relaxed">{useCase.description}</p>
                <div className="bg-background p-4 rounded border border-primary/10">
                  <p className="text-sm font-mono text-charcoal whitespace-pre-wrap">{useCase.example}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Social Proof Section */}
      <section className="py-20 sm:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Business owners are already saving time</h2>
            <p className="text-warm-gray text-lg">See what's working for people like you</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                quote: 'Saves me 3 hours a week on customer emails. Finally time to focus on actually growing the business instead of drowning in replies.',
                author: 'Linda Torres',
                role: 'Owner, Sunrise Bakery',
                initials: 'LT',
                avatarClass: 'bg-violet-100 text-violet-700',
              },
              {
                quote: 'I was nervous about using AI, but they explained it so clearly. Now I use it for proposals, thank-you notes, and follow-ups.',
                author: 'James Okafor',
                role: 'Plumbing contractor',
                initials: 'JO',
                avatarClass: 'bg-blue-100 text-blue-700',
              },
              {
                quote: 'Hired TheNormal to help me understand ChatGPT for my salon. Ended up using it for scheduling, thank-yous, and special offers.',
                author: 'Maria Garcia',
                role: 'Owner, Hair & Wellness',
                initials: 'MG',
                avatarClass: 'bg-pink-100 text-pink-700',
              },
            ].map((testimonial, i) => (
              <div key={i} className="p-8 bg-background rounded-lg border border-gray-200">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, j) => (
                    <span key={j} className="text-warning">★</span>
                  ))}
                </div>
                <p className="text-charcoal mb-6 leading-relaxed italic">"{testimonial.quote}"</p>
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full ${testimonial.avatarClass} flex items-center justify-center text-sm font-semibold flex-shrink-0`}>
                    {testimonial.initials}
                  </div>
                  <div>
                    <p className="font-semibold text-charcoal">{testimonial.author}</p>
                    <p className="text-sm text-warm-gray">{testimonial.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 sm:py-28 bg-background">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Questions we get a lot</h2>
          </div>

          <div className="space-y-6">
            {[
              {
                q: 'Will this really save my business time?',
                a: 'It depends on what you do. If you spend more than 5 hours a week on email, customer replies, or repetitive writing—yes. If you barely use email—probably not worth it. We give you 5 free sessions so you can try before you commit.'
              },
              {
                q: 'Is AI actually safe for business stuff?',
                a: "Use it for things that don't need to be secret (emails, drafts, ideas). Don't paste customer data or financial info. Think of it like talking to a smart intern—helpful for brainstorming, not for sensitive stuff."
              },
              {
                q: "Will my customers notice I'm using AI?",
                a: "Only if you let them. We teach you how to write prompts so the response sounds like you, not a robot. Most of our users tweak the AI's draft a little before sending—makes it better anyway."
              },
              {
                q: 'What if I get stuck?',
                a: "That's what we're here for. Chat with our AI helper any time. If it can't solve it, we're building a way to talk to actual humans (coming soon). No robots all the way down—promise."
              }
            ].map((faq, i) => (
              <div key={i} className="p-6 bg-white rounded-lg border border-gray-200">
                <h3 className="font-semibold text-charcoal mb-3 text-lg">{faq.q}</h3>
                <p className="text-warm-gray leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 sm:py-28 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">Ready to get your time back?</h2>
          <p className="text-lg text-warm-gray mb-8">
            Try it free. No credit card. 5 sessions to see if it clicks.
          </p>

          <Link href="/signup" onClick={() => analytics.ctaClicked({ location: 'footer_cta', cta: 'signup', persona: 'business_owner' })}>
            <Button className="bg-primary hover:bg-primary-700 text-white px-8 py-6 text-lg">
              Get Started Now
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  )
}
